import FormattedEther from '@/presentation/components/elements/FormattedEther';
import Icon, { Icons } from '@/presentation/components/elements/Icon';
import VaultIcon from '@/presentation/components/elements/VaultIcon';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslations } from 'use-intl';
import type { SetRangeFormFields } from '../types';
import { formatEther, parseEther } from 'viem';

type Form = UseFormReturn<SetRangeFormFields>;

interface Props {
  symbol: string;
  poolType: 'pair' | 'eth' | 'nft';
  vaultId?: string;
  icon: string | undefined;
  control: Form['control'];
  setDisabled: (v: boolean) => void;
  onEthAmountChange: (v: string) => void;
  onTokenAmountchange: (v: string) => void;
  setAmountController: (v: 'ethAmount' | 'tokenAmount') => void;
  name: 'ethAmount' | 'tokenAmount';
  max: bigint | undefined;
  using: 'vtoken' | 'nft';
}

export const Amount = ({
  symbol,
  vaultId,
  icon,
  control,
  poolType,
  setDisabled,
  onEthAmountChange,
  onTokenAmountchange,
  setAmountController,
  name,
  max = 0n,
  using,
}: Props) => {
  // formatting
  const t = useTranslations('CreateLiquidityPositionForms.SetRange');
  // type of pool
  const isEthOnly = poolType === 'eth';
  const isNftOnly = poolType === 'nft';

  const { price = '0' } = useWatch({
    control,
  });

  const hasPrice = !!price && !!Number(price) && !Number.isNaN(Number(price));

  const isLocked =
    (name === 'ethAmount' && isNftOnly) ||
    (name === 'tokenAmount' && isEthOnly);
  const isFixedAmount = isNftOnly && using === 'nft';
  const isDisabled = isLocked || isFixedAmount || !hasPrice;

  return (
    <Controller
      key={name}
      name={name}
      control={control}
      rules={{
        validate: (v) => {
          if (!v) {
            return 'Required';
          }
          const value = parseEther(v);
          if (!isLocked && value === 0n) {
            return 'Please enter an amount';
          }
          if (value < 0n) {
            return 'Must be positive';
          }
          if (value > max) {
            return 'Insufficient funds';
          }
          return true;
        },
      }}
      render={({ field, fieldState: { error } }) => (
        <div>
          <div className="relative rounded-lg p-4 overflow-hidden border border-gray-300 bg-gray-50 hover:border-pink-500/50">
            {isLocked && (
              <div className="absolute inset-0 bg-gray-200 bg-opacity-90 grid place-items-center font-bold text-sm text-center p-4">
                <Icon name={Icons.LOCK_CLOSED} />
                {t(`locked.${isNftOnly ? 'nft' : 'eth'}`)}
              </div>
            )}
            <div className="flex items-center">
              <div className="bg-gray-50 border-gray-300 border rounded-md px-3 py-2 flex items-center">
                {name === 'ethAmount' ? (
                  <Icon
                    name={Icons.ETH}
                    className="w-5 h-5 mr-2 flex-none text-blue-500"
                    size={20}
                  />
                ) : vaultId != null ? (
                  <VaultIcon
                    vaultId={vaultId}
                    className="w-5 h-5 mr-2 flex-none"
                  />
                ) : icon ? (
                  <img
                    src={icon}
                    className="w-5 h-5 mr-2 flex-none"
                    alt={symbol}
                  />
                ) : (
                  <span className="rounded-full flex-none w-5 h-5 mr-2 bg-gray-400" />
                )}
                <span className="uppercase font-bold text-sm truncate">
                  {symbol}
                </span>
              </div>
              <div className="flex-1 flex items-end overflow-hidden">
                <input
                  {...field}
                  className="font-sans text-2xl text-gray-800 text-right w-full bg-transparent outline-0 flex-1 border-0 focus:ring-0 pr-0 p-0 pl-4 truncate placeholder:text-gray-400"
                  placeholder="0"
                  type="number"
                  disabled={isDisabled}
                  onChange={(e) => {
                    setAmountController(name);
                    field.onChange(e);
                    setDisabled(true);
                    if (name === 'ethAmount') {
                      onEthAmountChange(e.target.value);
                    } else {
                      onTokenAmountchange(e.target.value);
                    }
                  }}
                />
              </div>
            </div>
            <div className="mt-2 whitespace-nowrap flex">
              <span className="text-sm text-gray-400 font-medium">
                {t.rich('balance', {
                  count: (
                    <FormattedEther value={max} key={`${name}Max`} />
                  ) as any,
                })}
                {!isDisabled && (
                  <button
                    type="button"
                    onClick={() => {
                      const value = formatEther(max);
                      setAmountController(name);
                      field.onChange(value);
                      setDisabled(true);
                      if (name === 'ethAmount') {
                        onEthAmountChange(value);
                      } else {
                        onTokenAmountchange(value);
                      }
                    }}
                    className="font-bold text-pink-500 hover:underline uppercase ml-1"
                  >
                    {t('max')}
                  </button>
                )}
              </span>
            </div>
          </div>
          {!!error && <div className="text-red-500">{error.message}</div>}
        </div>
      )}
    />
  );
};

export default Amount;
